// Returns an array of filters and the data used to render them parsed from
// ThoughtSpot's search results data structure.
export const parseFilters = (searchData) => {
  return searchData?.filterGroups?.map((filterGrp) => parseFilterGroup(filterGrp))?.flat() ?? [];
};

// Filter groups are one or more filters that use the same field or formula.
// Returns an array of filters and the data used to render them from the
// given filter group.
const parseFilterGroup = (filterGrp) => {
  const name = parseFilterName(filterGrp);
  const dataType = parseFilterDataType(filterGrp);
  return filterGrp?.filters?.map((filter) => parseFilter(filter, name, dataType));
};

// Returns an object containing data necessary for rendering a single filter
const parseFilter = (filter, name, dataType) => {
  return {
    name,
    dataType,
    op: parseFilterOp(filter),
    values: parseFilterValues(filter),
  };
};

// Returns the field or formula name for the given filter group
const parseFilterName = (filterGrp) => {
  return filterGrp?.columnInfo?.name;
};

// Returns the data type of the given filter group
const parseFilterDataType = (filterGrp) => {
  return filterGrp?.columnInfo?.dataType;
};

// Returns an object representing the type (i.e. operation) performed by the
// given filter, as well as a boolean indicating if it should negated.
// See formattedOps constant above for supported types.
const parseFilterOp = (filter) => {
  const { filterType: type, negate } = filter?.filterContent?.[0];
  return { type, negate };
};

// Returns an array of valid values for the given filter
const parseFilterValues = (filter) => {
  return filter?.filterContent?.[0]?.value?.map((val) => val.key === null ? 'NULL' : val.key);
};

const AUDIENCE_SIZE_COLUMN_NAMES = [
  'Profiles',
  'Profile Count',
  'Shoppers',
  'Shopper Profiles',
  'Shopper Count',
  'Consumers',
  'Consumer Count',
];

// Searches ThoughtSpot's search results data structure for columns that look
// like audience sizes and returns the first value found amongst them. Returns
// null if no valid audience size can be found in search results.
export const parseAudienceSize = (searchData) => {
  const { embedAnswerData: { columns = [], data = [] } } = searchData;
  // Find all columns in the search results that could be audience sizes
  const audienceSizeColumnIds = columns?.map((col) => col.column)
    ?.filter((col) => AUDIENCE_SIZE_COLUMN_NAMES.includes(col?.name))
    ?.map((col) => col.id);
  // Return the first value we find that has a matching column id with above
  return (Array.isArray(data) ? data : [data])
    ?.map((el) => el.columnDataLite)
    ?.flat()
    ?.find((colData) => audienceSizeColumnIds.includes(colData.columnId))
    ?.dataValue
    ?.reduce((acc, curr) => acc + curr)
    ?? null;
};
